import type { NavigationType } from 'react-router-dom';
import { SCROLL_RESTORE_STATE_KEY } from '../hooks/useScrollRestore';

export type SavedLibraryGridState = {
  /** Filter/sort/search signature — must match on restore. */
  scrollKey: string;
  scrollTop: number;
  /** Album rows loaded when scroll was saved (pagination depth). */
  loadedCount: number;
  /** Virtualizer row window when scroll was saved. */
  windowStartRow?: number;
  windowEndRow?: number;
  albums: Record<string, unknown>[];
  albumTotalCount: number;
  timestamp: number;
};

const scrollMemory = new Map<string, SavedLibraryGridState>();

const SESSION_TTL_MS = 30 * 60 * 1000;

export function buildLibraryGridScrollKey(state: {
  activeCategory: string;
  searchQuery: string;
  selectedArtist: string | null;
  selectedGenre: string | null;
  selectedComposer: string | null;
  selectedLabel: string | null;
  activeFolderPath: string | null;
  viewMode: string;
  albumWallSort: string;
  activeFilters: Record<string, unknown>;
}): string {
  const f = state.activeFilters || {};
  return [
    state.activeCategory,
    state.searchQuery,
    state.selectedArtist ?? '',
    state.selectedGenre ?? '',
    state.selectedComposer ?? '',
    state.selectedLabel ?? '',
    state.activeFolderPath ?? '',
    state.viewMode,
    state.albumWallSort,
    f.preset ?? '',
    f.artwork ?? '',
    f.format ?? '',
    f.sampleRate ?? '',
    f.bitDepth ?? '',
    f.sourceId ?? '',
    f.recentlyAdded ?? '',
  ].join('|');
}

/** Album-wall scroll signature — omits sidebar tab so Artists ↔ Albums retains offset. */
export function buildAlbumWallScrollKey(
  state: Omit<Parameters<typeof buildLibraryGridScrollKey>[0], 'activeCategory'>,
): string {
  return buildLibraryGridScrollKey({ ...state, activeCategory: 'albums' });
}

export function hasLibraryGridScroll(key: string): boolean {
  const saved = scrollMemory.get(key);
  if (!saved) return false;
  if (Date.now() - saved.timestamp > SESSION_TTL_MS) return false;
  return saved.scrollKey === key;
}

export function shouldRestoreLibraryGridScroll(
  locationState: unknown,
  navType: NavigationType,
  scrollKey?: string,
): boolean {
  const scrollRestoreFlag = (locationState as Record<string, unknown> | null)?.[SCROLL_RESTORE_STATE_KEY] === true;
  if (navType === 'POP' || scrollRestoreFlag) return true;
  if (navType === 'REPLACE' && scrollKey && hasLibraryGridScroll(scrollKey)) return true;
  return false;
}

export type LibraryGridScrollSaveOptions = {
  /** Diagnostic / guard — e.g. unmount must not clobber album-open offset. */
  reason?: string;
};

export function saveLibraryGridScroll(
  key: string,
  snapshot: {
    scrollTop: number;
    loadedCount: number;
    albums: Record<string, unknown>[];
    albumTotalCount: number;
    scrollKey?: string;
    windowStartRow?: number;
    windowEndRow?: number;
  },
  options?: LibraryGridScrollSaveOptions,
): void {
  if (snapshot.scrollTop <= 0 && snapshot.loadedCount <= 0 && snapshot.albums.length <= 0) return;
  const existing = scrollMemory.get(key);
  if (
    options?.reason === 'unmount'
    && snapshot.scrollTop <= 0
    && existing
    && existing.scrollTop > 0
    && existing.scrollKey === key
  ) {
    return;
  }
  scrollMemory.set(key, {
    scrollKey: snapshot.scrollKey ?? key,
    scrollTop: snapshot.scrollTop,
    loadedCount: snapshot.loadedCount,
    windowStartRow: snapshot.windowStartRow,
    windowEndRow: snapshot.windowEndRow,
    albums: snapshot.albums.slice(),
    albumTotalCount: snapshot.albumTotalCount,
    timestamp: Date.now(),
  });
}

/** Read a saved grid snapshot without removing it (forward-nav remount). */
export function peekLibraryGridScroll(key: string): SavedLibraryGridState | null {
  const saved = scrollMemory.get(key);
  if (!saved) return null;
  if (Date.now() - saved.timestamp > SESSION_TTL_MS) {
    scrollMemory.delete(key);
    return null;
  }
  if (saved.scrollKey !== key) return null;
  return saved;
}

/** Read and remove a saved grid snapshot for this signature (back-nav restore). */
export function takeLibraryGridScroll(key: string): SavedLibraryGridState | null {
  const saved = peekLibraryGridScroll(key);
  if (!saved) return null;
  scrollMemory.delete(key);
  return saved;
}

export function clearLibraryGridScroll(key?: string): void {
  if (key) scrollMemory.delete(key);
  else scrollMemory.clear();
}
